// How the offer rule reached an item's buy price, as text for the 🧮 Price breakdown.
// Pure: `rule` is the `offerRule` object of GET /api/deals/:id/items/:id/bp-factors,
// which the pricing engine computes (pricing-worker docs/specs_20261005_offer_rule.md).
// Nothing here recalculates a price. It only names the steps the engine returned.

const BAND_LABELS = ['<£100', '£100–250', '£250–500', '£500–1000', '£1000+'];
// The engine sends the size of each cut with its answer (cutsPct). These only cover an
// answer from an engine build older than that field.
const DEFAULT_CUTS_PCT = { oversupply: 7, thinData: 15 };
const cutsPct = (rule) => ({ ...DEFAULT_CUTS_PCT, ...(rule?.cutsPct || {}) });

const signed = (n) => (n > 0 ? `+${n}` : `−${Math.abs(n)}`);
const pctOf = (ratio) => Math.round(ratio * 1000) / 10;
const spreadCutPct = (rule) => Math.round((rule?.facts?.spreadCut ?? 0) * 100);

// The category whose ladder and points the rule used, and how it was matched.
export function ruleCategory(rule) {
    const name = rule.subCategory || rule.mainCategory || 'no category';
    const band = BAND_LABELS[rule.band] ?? null;
    const fallback = rule.matchLevel === 'sub' ? null
        : rule.matchLevel === 'main' ? 'main category entry' : 'default ladder';
    return { label: band ? `${name} · ${band}` : name, fallback };
}

// "57% base · category +2 · demand +5 · condition −2 = 62%". Zero parts are left out.
export function rulePointsLine(rule) {
    const p = rule.points || {};
    const parts = [`${pctOf(p.ladderPct ?? 0)}% base`];
    if (p.categoryPp) parts.push(`category ${signed(p.categoryPp)}`);
    if (p.demandPp) parts.push(`demand ${signed(p.demandPp)}`);
    if (p.conditionPp) parts.push(`condition ${signed(p.conditionPp)}`);
    return `${parts.join(' · ')} = ${pctOf(rule.pct ?? 0)}%`;
}

// The market cuts taken after the percentage, one chip each.
export function ruleCuts(rule) {
    const f = rule.facts || {};
    const cuts = [];
    const pct = cutsPct(rule);
    // The rule fires when listings are at least twice the sales (price-calculation.js).
    const why = f.listedCount != null && f.soldCount != null
        ? `${f.listedCount} listed is at least 2 × ${f.soldCount} sold`
        : 'Few sales against many listings';
    if (f.oversupplied) cuts.push({ key: 'oversupply', label: `oversupplied −${pct.oversupply}%`, tip: `${why}, so the offer is cut by ${pct.oversupply}%.` });
    if (f.thinData) cuts.push({ key: 'thin_data', label: `thin data −${pct.thinData}%`, tip: `Too little market data to trust the resale price fully, so the offer is cut by ${pct.thinData}%.` });
    if (spreadCutPct(rule) > 0) cuts.push({ key: 'spread', label: `price spread −${spreadCutPct(rule)}%`, tip: 'Sold prices are scattered, so the resale price is less certain and the offer is cut.' });
    return cuts;
}

// One row per step the engine took, ending on the buy price. The value of a row is the
// running offer after that step, in whole pounds.
export function ruleBreakdownRows(rule, sp) {
    const p = rule.points || {};
    const f = rule.facts || {};
    const category = rule.subCategory || rule.mainCategory;
    const labels = {
        ladder: `${pctOf(p.ladderPct ?? 0)}% base for ${ruleCategory(rule).label}`,
        category: `Category ${signed(p.categoryPp ?? 0)} points${category ? ` (${category})` : ''}`,
        condition: `Condition ${signed(p.conditionPp ?? 0)} points (${rule.grade})`,
        demand: `Demand ${signed(p.demandPp ?? 0)} points (${f.demandTier ?? 'no tier'})`,
        oversupply: `Oversupplied −${cutsPct(rule).oversupply}%`,
        thin_data: `Thin data −${cutsPct(rule).thinData}%`,
        spread: `Price spread −${spreadCutPct(rule)}%`,
        shipping: `Shipping −£${f.shippingCost ?? 0}`,
        not_working_guard: 'Not working and worth less than its shipping',
        rounding: 'Rounding (up to the next £50)',
    };
    const rows = [];
    const price = Number(sp);
    if (Number.isFinite(price) && price > 0) rows.push({ key: 'sp', label: 'SP', value: Math.round(price) });
    // The percentage is taken on a lower figure than the SP when a UK scarcity premium
    // raised the SP, or when the comps were collection-only.
    if (Number.isFinite(rule.basis) && Number.isFinite(price) && Math.round(rule.basis) !== Math.round(price)) {
        rows.push({
            key: 'basis', value: Math.round(rule.basis),
            label: f.pricingMode === 'COLLECTION_FALLBACK' ? 'Priced on (collection-only comps)' : 'Priced on (before scarcity premium)',
        });
    }
    for (const s of rule.steps || []) rows.push({ key: s.step, label: labels[s.step] ?? s.step, value: s.bp });
    rows.push({ key: 'bp', label: 'BP', value: rule.bp, final: true });
    return rows;
}
